import clsx from "clsx"
import Image from "next/image"

import { FlexBox } from "@components/wrapper"
import { CourseMaterial, SubModuleType } from "@interfaces/course/course-detail"
import { HARISENIN_PUBLIC_ICON } from "@constants/const/pictures"
import { CourseHeading } from "."
import { Disclosure } from "@headlessui/react"
import { FaChevronDown } from "react-icons/fa"

const SubModuleLabel = ({ type }: { type: SubModuleType }) => {
  let component

  switch (type) {
    case "test":
      component = (
        <>
          <Image
            src={`${HARISENIN_PUBLIC_ICON}/icon_pencil.svg`}
            width={18}
            height={18}
            alt="test"
            className="w-[18px] h-[18px]"
            quality={100}
          />
          <div className="text-sm font-semibold">Test</div>
        </>
      )
      break
    case "video":
      component = (
        <>
          <Image
            src={`${HARISENIN_PUBLIC_ICON}/icon_video.svg`}
            width={18}
            height={18}
            alt="video"
            className="w-[18px] h-[18px]"
          />
          <div className="text-sm font-semibold">Video</div>
        </>
      )

      break
    case "document":
      component = (
        <>
          <Image
            src={`${HARISENIN_PUBLIC_ICON}/icon_document-download.svg`}
            width={20}
            height={20}
            alt="document"
            className="wh-5"
          />
          <div className="text-sm font-semibold">Dokumen</div>
        </>
      )
      break
    default:
      break
  }

  return (
    <FlexBox className="text-sm font-semibold w-1/2 gap-3" align="center">
      {component}
    </FlexBox>
  )
}

interface ClassMaterialProps {
  data: CourseMaterial
  duration: string
}

export const CourseMaterialList = ({ data, duration }: ClassMaterialProps) => {
  const CARD_CLASS = clsx(
    "bg-white rounded-lg sm:px-[30px] sm:py-4",
    "px-2.5 py-3 justify-between w-full"
  )

  return (
    <section className={clsx("bg-[#F6F6F7] rounded sm:px-10 sm:py-[54px]", "px-4 py-4")}>
      <div className="sm:mb-8 mb-5">
        <CourseHeading customMargin={clsx("sm:mb-5 mb-2")}>Kamu Akan Mempelajari</CourseHeading>

        <div className="font-semibold text-grey-97 sm-only:text-xs">
          Total Waktu Pembelajaran <span className="text-black">{duration}</span>
        </div>
      </div>

      <div className="grid gap-2">
        {/* Pretest */}
        <FlexBox className={CARD_CLASS}>
          <div className={clsx("sm:w-1/2 font-semibold sm:text-base", "text-sm")}>Pretest</div>

          <FlexBox className={clsx("sm:flex-1", "")} align="center">
            <FlexBox align="center" className={clsx("sm:mr-0 sm:gap-3.5 sm:w-1/2", "mr-2 gap-2.5")}>
              <Image
                src={`${HARISENIN_PUBLIC_ICON}/icon_pencil.svg`}
                width={20}
                height={20}
                className="sm:wh-5 wh-3"
                alt="pretest"
                quality={100}
              />
              <div className="sm:font-semibold sm:text-sm text-xs">
                {data.course_pretest.total_question} Soal
              </div>
            </FlexBox>

            <FlexBox className={clsx("text-sm sm:font-semibold gap-3 sm:w-1/2")} align="center">
              {/* <Image
                src={`${HARISENIN_PUBLIC_ICON}/icon_minute.svg`}
                width="16"
                height={16}
                alt="minute"
              />
              <div className="font-semibold sm:text-sm text-[10px]">
                {data.course_pretest.duration_minutes}
              </div> */}
            </FlexBox>
          </FlexBox>
        </FlexBox>

        {/* Modules */}
        {data.course_modules.map((value, index) => (
          <div key={`item-${index}`} className={clsx(CARD_CLASS)}>
            <Disclosure>
              {({ open }) => (
                <>
                  <Disclosure.Button className={clsx("sm:flex w-full")}>
                    <div
                      className={clsx(
                        "sm:w-1/2 font-semibold text-left sm-only:text-sm sm-only:mb-2.5"
                      )}
                    >
                      {value.module_name}
                    </div>

                    <FlexBox className="relative sm:w-1/2">
                      <FlexBox
                        align="center"
                        className={clsx("sm:w-1/2 sm:gap-4", "gap-2.5 w-fit sm-only:mr-5")}
                      >
                        <Image
                          src={`${HARISENIN_PUBLIC_ICON}/icon_video.svg`}
                          width={20}
                          height={20}
                          className="sm:wh-5 wh-3"
                          alt="video"
                        />
                        <div className="sm:text-sm sm:font-semibold text-xs">
                          {value.module_videos_total} Video
                        </div>
                      </FlexBox>

                      <FlexBox align="center" className="sm:w-1/2 sm:gap-3 gap-2.5">
                        <Image
                          src={`${HARISENIN_PUBLIC_ICON}/icon_clock.svg`}
                          width={20}
                          height={20}
                          className="sm:wh-5 wh-3"
                          alt="minutes"
                        />
                        <div className="sm:text-sm sm:font-semibold text-xs">
                          {value.module_duration_total} Menit
                        </div>
                      </FlexBox>

                      <div className="absolute right-0 sm-only:-top-6">
                        <FaChevronDown
                          className={clsx(open && "rotate-180", "text-green sm-only:text-sm")}
                        />
                      </div>
                    </FlexBox>
                  </Disclosure.Button>

                  <Disclosure.Panel>
                    <div className="sm:h-[40px] h-[24px]" />

                    <FlexBox direction="col" className="sm:gap-8 gap-3">
                      {value.sub_modules.map((value1, index1) => (
                        <FlexBox key={`item1-${index1}`} align="center" className="w-full">
                          <div
                            className={clsx(
                              "sm:text-sm sm:font-semibold sm:w-1/2 pr-4",
                              "text-xs w-full"
                            )}
                          >
                            {index1 + 1}. {value1.sub_module_title}
                          </div>

                          <div className={clsx("sm:flex hidden sm:w-1/2", "justify-end")}>
                            <SubModuleLabel type={value1.sub_module_type} />
                            {value1.sub_module_type === "video" && (
                              <FlexBox
                                className={clsx("text-sm sm:font-semibold gap-3 w-1/2")}
                                align="center"
                              >
                                <Image
                                  src={`${HARISENIN_PUBLIC_ICON}/icon_clock.svg`}
                                  width={18}
                                  height={18}
                                  alt="minutes"
                                />
                                <div className="font-semibold text-sm">{value1.duration} Menit</div>
                              </FlexBox>
                            )}
                          </div>
                        </FlexBox>
                      ))}
                    </FlexBox>

                    <div className="sm:h-4 h-[24px]" />
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          </div>
        ))}

        {/* Final Test */}
        <FlexBox className={clsx(CARD_CLASS)}>
          <div className={clsx("sm:w-1/2 font-semibold sm:text-base", "text-sm")}>Ujian Akhir</div>
          <FlexBox
            align="center"
            className={clsx("sm:flex-1", "sm-only:justify-between sm-only:w-max sm-only:gap-5")}
          >
            <FlexBox align="center" className={clsx("sm:flex-1 sm:mr-0 sm:gap-3.5", "gap-2.5")}>
              <Image
                src={`${HARISENIN_PUBLIC_ICON}/icon_pencil.svg`}
                width={20}
                height={20}
                className="sm:wh-5 wh-3"
                alt="test"
              />
              <div className="sm:font-semibold sm:text-sm text-xs">
                {data.course_post_test.total_question} Soal
              </div>
              <div className="sm:block hidden" />
            </FlexBox>

            <FlexBox className={clsx("text-sm sm:font-semibold gap-3 sm:w-1/2")} align="center">
              {/* <Image
                src={`${HARISENIN_PUBLIC_ICON}/icon_minute.svg`}
                width="16"
                height={16}
                alt="minutes"
              />
              <div className="font-semibold sm:text-sm text-[10px]">
                {data.course_post_test.duration_minutes}
              </div> */}
            </FlexBox>
          </FlexBox>
        </FlexBox>
      </div>
    </section>
  )
}
